import type { Ceviri } from "../tip";
import type { kilit as tr } from "../tr/kilit";

/** English dictionary · kilit. */
export const kilit: Ceviri<typeof tr> = {
  parola: "Password",
  profil: "Profile",
  profilAdi: "Profile name",
  kurtarmaIfadesi: "Recovery phrase",
  yeniParola: "New password",
  yeniProfilEkle: "Add new profile",
  /** Gizlilik perdesindeki logonun metni. */
  perde: "Parafon",

  ekran: {
    baslik: "Vault locked",
    aciliyor: "Unlocking…",
    ac: "Unlock",
    parolamiUnuttum: "I forgot my password",
    ifadeAciklama:
      "Type the 24 words you wrote on paper during setup, in order, separated by spaces. Upper or lower case does not matter; the first four letters of each word are enough. Then set a new password.",
    ifadeyleAc: "Unlock with phrase",
    parolaylaAc: "Unlock with password",
    yeniProfilAciklama:
      "A separate vault on the same device: its own password, its own 24 words. Existing profiles are not affected.",
  },

  kurulum: {
    profileAdVerin: "Give the profile a name.",
    yeniProfil: "New profile",
    sifreleyelim: "Let's encrypt your vault",
    kuralim: "Let's set up your vault",
    nasilBaslayalim: "How to start",
    yeniKasaKur: "Set up a new vault",
    yeniKasaKurAciklama: "An empty vault, a new password and 24 words.",
    buluttanGetir: "Restore from cloud",
    buluttanGetirAciklama: "Your vault from another device, with its vault code, a QR code or your cloud account.",
    tasimaAciklama:
      "Parafon now keeps your vault ENCRYPTED on this device. Your current vault will be moved once, now, encrypted; this step cannot be skipped. The old copy is not touched until the move is verified.",
    yeniAciklama:
      "Your vault is kept encrypted on this device. You will use a password to open it, and a 24-word recovery phrase in case you forget the password.",
    parolaKorur:
      "The same password also protects your cloud backup. The vault cannot be opened without the password or the recovery phrase — Parafon knows neither.",
    durumOkunuyor: "Reading status…",
    profilAyriAciklama:
      "This profile is completely separate from the other profiles on this device: its own password, its own 24 words, its own cloud storage. To bring your vault from another device, choose “Restore from cloud” above.",
    profilAdiIpucu: "Visible to everyone on the lock screen; it does not have to be your real name.",
    sifreleniyor: "Encrypting vault…",
    kuruluyor: "Setting up…",
    sifreleVeTasi: "Encrypt and move",
    kur: "Set up",
    sifrelendiBaslik: "Your vault is encrypted",
    sifrelendi: (n: number) =>
      `Your vault (${n} ${n === 1 ? "transaction" : "transactions"}) was encrypted, moved and verified; the unencrypted old copy was deleted.`,
    kurtarmaIfadeniz: "Your recovery phrase",
    kasaAciliyor: "Opening vault…",
  },

  getir: {
    yedekYok: "There is no Parafon backup here. Make sure you connected to the right place.",
    aciklama:
      "Bring here the vault you use on another device. That vault's password or 24 words apply; you do not set a new password.",
    baglanVeListele: "Connect and list backups",
    kasanizNerede: "Where is your vault",
    yedekDosyasi: "Backup file (newest selected)",
    neyleAcacaksiniz: "How will you open the vault",
    parolayla: "With password",
    ifadeyle: "With recovery phrase (24 words)",
    ifadeIpucu: "Words in order, separated by spaces; the first four letters are enough.",
    kasaninParolasi: "Vault password",
    profilAdiIpucu: "If left empty, the vault name in the backup is used.",
    getiriliyor: "Restoring…",
    kasayiGetir: "Restore vault",
    baskaYereBaglan: "Connect elsewhere",
    dosyadan: "Or from a backup file on this device (.pfai)",
    dosyaSec: "Choose file",
    secilenDosya: (ad: string) => `Chosen file: ${ad}`,
  },

  kayip: {
    baslik: "Vault not found on this device",
    neOlmus: "What may have happened",
    neOlmusMetin:
      "Your password is correct, but the encrypted vault files are missing from this browser or cannot be read. The browser may have cleared its storage. If you have a cloud backup, you can restore your vault from there.",
    bosKasaylaBasla: "Start with an empty vault, then restore from cloud",
    bosKasaAciklama:
      "An empty vault will be opened on this device. Then restore your cloud backup from Settings or the Accounts screen.",
    evetBasla: "Yes, start with an empty vault",
  },

  ifade: {
    kagidaYazin: "Write these 24 words on paper",
    kagidaYazinMetin:
      "If you forget your password, these words are the ONLY way to open your vault and your cloud backup. Parafon does not store them and cannot show them again. Do not take a screenshot or save them to a file: write them on paper and keep it somewhere safe.",
    yazdim: "I wrote the 24 words on paper, in order",
    tutmadi: (sira: number) =>
      `Word ${sira} does not match. Check the list on paper; show the words again if needed.`,
    dogrulamaAciklama: "To check that you wrote them down correctly, type these words:",
    kelime: (sira: number) => `Word ${sira}`,
    dogrula: "Verify",
    yenidenGoster: "Show words again",
  },

  bant: {
    yazilamadi: (hata: string) =>
      `Recent changes could NOT be saved to the device: ${hata}. Do not close the tab; free up space and make another change, or take a backup from Settings.`,
    oncekiHal:
      "The last save was incomplete; the previous state of the vault was opened. Changes from the last few seconds may be missing.",
    dogrulanmadi: "You have not verified your recovery phrase: if you forget your password, the vault cannot be opened. ·",
    yeniIfadeAlin: "Get a new phrase in Settings",
  },

  gizleme: {
    perdeSuresi: "Privacy screen when idle",
    perdeAciklama: "If the screen is not touched for this long, a privacy screen covers it; a touch lifts it. This device only.",
    kapali: "Off",
    saniye: (n: number) => `${n} s`,
    acilistaGizle: "Hide amounts when the vault opens",
    perdedeGizle: "Hide amounts when the privacy screen comes down",
  },

  tani: {
    baslik: "Privacy screen diagnostics",
    aciklama: "Signals the browser gave on recent switches; kept only on this device.",
    bos: "No entries yet: switch to the app switcher and back, then refresh.",
    gorunur: "visible",
    gizli: "hidden",
    odakVar: "focused",
    odakYok: "not focused",
    yenile: "Refresh",
    temizle: "Clear",
  },

  kart: {
    yolTasi: "Safe move — a new file is written and replaces the old one",
    yolIkiYuva: "Two-slot write — the browser does not support moving files, two copies alternate",
    sunucuParolaKisa: (n: number) =>
      `Your cloud backup is on the Parafon server: the password must be at least ${n} characters.`,
    parolaDegisti: "Password changed. From now on the cloud backup also opens with the new password.",
    baslik: "Vault lock",
    aciklama: "The vault is encrypted on this device. Once locked, the password (or recovery phrase) is needed to open it.",
    simdiKilitle: "Lock now",
    kendiligindenKilitle: "Auto-lock when idle",
    dakikaSonra: (dk: number) => `After ${dk} ${dk === 1 ? "minute" : "minutes"}`,
    parolayiDegistir: "Change password",
    yeniIfadeOlustur: "Create new recovery phrase",
    yeniIfadeAciklama: "The old phrase stops working. Use this if you lost the paper or did not verify during setup.",
    yeniIfadeDogrulandi: "New recovery phrase verified; the old one no longer works.",
    tamam: "Done",
    acikProfil: "Open profile:",
    profilSayisi: (n: number) => ` · ${n} ${n === 1 ? "profile" : "profiles"} on this device`,
    adiniDegistir: "Rename",
    profilDegistir: "Switch profile",
    profiliSilSor: "Delete this profile from this device",
    yeniProfilKilitli: "Adding a new profile (extra vault) to this device",
    silBaslik: "Profile will be deleted from this device",
    silMetin: (ad: string) =>
      `The vault, local backups and record of profile “${ad}” on this device will be deleted; this cannot be undone. Cloud backups are not touched — they can be restored to another device or a new profile.`,
    silOnay: (ad: string) => `To confirm, type the profile name: ${ad}`,
    profiliSil: "Delete profile",
    baskaKasaAc: "Open another vault on this device",
    baskaKasaAciklama: "Replaces the vault on this device with one you backed up to the cloud from another device.",
    sifreliYazim: (yol: string) => `Encrypted write: ${yol}.`,
  },

  parolaAlan: {
    bos: "Password cannot be empty.",
    enAz: (n: number) => `The new password must be at least ${n} characters.`,
    ayniDegil: "The two passwords do not match.",
    tekrar: (etiket: string) => `${etiket} (again)`,
    kisaBaslik: (n: number) => `Password shorter than ${n} characters`,
    kisaSart: (n: number) =>
      `When resetting the password, at least ${n} characters are required. A sentence of four or five words is easy to remember and much stronger.`,
    kisaUyari:
      "If your cloud backup is copied, a short password can be guessed. A sentence of four or five words is easy to remember and much stronger. You can still continue with this password.",
  },
};
